import { useClerk } from '@clerk/react-router';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { UserContext } from '~/app';
import { AuthLoading } from '~/pages/authentication/AuthLoading';
import { SatelliteAuthUnavailable } from '~/pages/authentication/SatelliteAuthUnavailable';
import { satelliteHandoffGuard } from '~/pages/authentication/satelliteSyncGuard';
import { SignInPage } from '~/pages/authentication/SignInPage';
import { authDebug, summarizeUrl } from './authDebug';
import { hasNestedClerkHash } from './clerkContinuation';
import {
  CLERK_SATELLITE_URL_PARAM,
  RETURN_TO_PARAM,
  buildAuthCompletePath,
  readAuthHandoffUrl,
  resolveSatelliteAuthCompleteUrl,
  sanitizeAuthHandoffUrl,
  sanitizeReturnTo,
} from './returnTo';
import { paths } from './routes';

const hasNestedClerkPath = (pathname: string, clerkPath: string): boolean => {
  if (!pathname.startsWith(`${clerkPath}/`)) return false;
  return pathname.slice(clerkPath.length).replace(/\//g, '').length > 0;
};

// [MCV-1789] This page is the primary's end of the cross-site hand-off, so it shows
// the primary's version of the unavailable page: its own retry (a different guard
// than the satellite's), wording that names the site we were sending them to, and
// a way back into the app that does not depend on the hand-off working.
const HandoffUnavailable = () => {
  // Providers (and impersonation, which resolves to the vault too) go straight to the
  // vault dashboard. /auth/complete would send an employer on to HealthcareLX through
  // the very hand-off that just failed, so everyone else gets the local home page.
  const isVaultUser = UserContext.useSelector(
    (state) =>
      state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_PROVIDER' }) ||
      state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );

  return (
    <SatelliteAuthUnavailable
      body="Your browser blocked the secure hand-off to HealthcareLX. This usually happens in private or incognito windows, and in browsers with stricter cross-site privacy protections."
      onRetry={() => {
        satelliteHandoffGuard.reset();
        window.location.reload();
      }}
      secondaryLink={
        isVaultUser
          ? { label: 'Go to my dashboard', href: paths.vault.dashboard }
          : { label: 'Go to the home page', href: paths.landing }
      }
    />
  );
};

const AuthenticatedSatelliteHandoff = ({ destination }: { destination: string }) => {
  const clerk = useClerk();
  const [failed, setFailed] = useState(false);
  // [MCV-1789] Decided once per mount, before anything is recorded, so recording
  // this hand-off cannot flip the page to "unavailable" under the user mid-redirect.
  const [blocked] = useState(() => satelliteHandoffGuard.isBlocked());
  // Effects run twice under React StrictMode and re-run when `clerk` or
  // `destination` change; one mount is one hand-off, so it is counted once.
  const recorded = useRef(false);

  useEffect(() => {
    if (blocked) {
      // The primary stays signed in for the whole of a bounce loop and never
      // reaches the satellite's own breaker, so this is the only place it can stop.
      authDebug('sign-in.authenticated_satellite_handoff_blocked', {
        destination: summarizeUrl(destination),
      });
      return;
    }

    let active = true;
    authDebug('sign-in.authenticated_satellite_handoff', {
      destination: summarizeUrl(destination),
    });

    if (!recorded.current) {
      recorded.current = true;
      satelliteHandoffGuard.record();
    }

    const redirect = async () => {
      try {
        await clerk.redirectWithAuth(destination);
      } catch {
        authDebug('sign-in.authenticated_satellite_handoff_failed', {
          destination: summarizeUrl(destination),
        });
        if (active) setFailed(true);
      }
    };

    void redirect();
    return () => {
      active = false;
    };
  }, [blocked, clerk, destination]);

  if (blocked || failed) return <HandoffUnavailable />;

  return <AuthLoading message="Connecting you to HealthcareLX…" />;
};

/**
 * Signed-in users normally have no Clerk sign-in UI to render. Route ordinary
 * sign-in entries through AuthComplete instead of mounting a blank Clerk
 * surface. Clerk still owns nested factor/session-task paths and a trusted
 * cross-origin HLX handoff, where mounting it is required to complete the
 * satellite cookie exchange.
 */
export const AuthenticatedSignInRoute = () => {
  const location = useLocation();
  const origin = window.location.origin;
  const clerkPath = location.pathname.startsWith(paths.publicAuth.signInLegacy)
    ? paths.publicAuth.signInLegacy
    : paths.publicAuth.signIn;

  if (hasNestedClerkPath(location.pathname, clerkPath) || hasNestedClerkHash(location.hash)) {
    return <SignInPage />;
  }

  const handoff = sanitizeAuthHandoffUrl(
    readAuthHandoffUrl(location.search, location.hash),
    origin
  );
  if (handoff) {
    const handoffUrl = new URL(handoff, origin);
    if (handoffUrl.origin !== origin) {
      return <AuthenticatedSatelliteHandoff destination={handoffUrl.toString()} />;
    }

    return <Navigate to={`${handoffUrl.pathname}${handoffUrl.search}${handoffUrl.hash}`} replace />;
  }

  // This carrier is emitted by Clerk's primary/satellite flow and is not the
  // same as an ordinary in-app return_to. Signed-in users have no SignIn flow
  // left to finalize, so send the normalized target through Clerk directly.
  // redirectWithAuth preserves the app destination and adds the development
  // browser token required for the satellite cookie exchange.
  //
  // [MCV-1789] `synced: true` mirrors clerk-js's own `#redirectToSatellite`: the
  // `__clerk_synced=true` stamp plus the dev-browser token that redirectWithAuth
  // adds. `__clerk_satellite_url` is only emitted for DEVELOPMENT instances
  // (production syncs through `/v1/client/sync`), so `true` cannot strand a
  // production satellite. The old `false` told the satellite to sync with us again,
  // so a satellite that could not read the hand-off hopped straight back here, and
  // we bounced it forever.
  const satelliteAuthCompleteUrl = resolveSatelliteAuthCompleteUrl(
    new URLSearchParams(location.search).get(CLERK_SATELLITE_URL_PARAM),
    { synced: true }
  );
  if (satelliteAuthCompleteUrl) {
    return <AuthenticatedSatelliteHandoff destination={satelliteAuthCompleteUrl} />;
  }

  const returnTo = sanitizeReturnTo(
    new URLSearchParams(location.search).get(RETURN_TO_PARAM),
    origin
  );
  return <Navigate to={buildAuthCompletePath(returnTo)} replace />;
};
